import { useInfiniteQuery, useMutation } from 'react-query';
import { getSceneContents, type SceneContentsResponse } from '../../services/sceneService';
import { SCENE_QUERY_KEYS } from '../queryKeys';

function getStableContentId(item: unknown): string | null {
  if (!item || typeof item !== 'object') return null;
  const anyItem = item as { sceneChatId?: unknown; sceneThreadId?: unknown };
  if (typeof anyItem.sceneChatId === 'string' && anyItem.sceneChatId) return anyItem.sceneChatId;
  if (typeof anyItem.sceneThreadId === 'string' && anyItem.sceneThreadId)
    return anyItem.sceneThreadId;
  return null;
}

function getOldestCursorFromContents(contents: unknown[] | undefined): string | undefined {
  if (!contents || contents.length === 0) return undefined;

  let oldest: { id: string; seq: number } | null = null;
  for (const item of contents) {
    const id = getStableContentId(item);
    const seq = (item as { sequence?: unknown })?.sequence;
    if (!id || typeof seq !== 'number') continue;
    if (oldest == null || seq < oldest.seq) oldest = { id, seq };
  }
  return oldest?.id;
}

export function useGetSceneContents(
  sceneId: string,
  scenePreviewId: string,
  options?: {
    enabled?: boolean;
    cursor?: string | null;
    direction?: 'after' | 'before';
    limit?: number;
  }
) {
  const { enabled = true, cursor, direction = 'after', limit } = options ?? {};

  return useInfiniteQuery(
    [...SCENE_QUERY_KEYS.sceneContents, sceneId, cursor, direction, limit],
    ({ pageParam }: { pageParam?: string }) =>
      getSceneContents({
        sceneId,
        scenePreviewId,
        cursor: pageParam ?? cursor ?? undefined,
        direction,
        limit
      }),
    {
      enabled: !!sceneId && !!scenePreviewId && enabled,
      staleTime: 60 * 1000,
      cacheTime: 5 * 60 * 1000,
      // direction=after: 최신 방향(다음 1개)만 nextCursor로 진행
      getNextPageParam: (lastPage) => {
        if (direction !== 'after') return undefined;
        const { pagination } = lastPage?.meta ?? {};
        return pagination?.nextCursor ?? undefined;
      },
      // direction=before: 과거 방향(이전 N개)만 prevCursor로 진행
      getPreviousPageParam: (firstPage) => {
        if (direction !== 'before') return undefined;
        const { pagination } = firstPage?.meta ?? {};
        return (
          pagination?.prevCursor ??
          getOldestCursorFromContents(firstPage?.sceneContents) ??
          undefined
        );
      }
    }
  );
}

export function useGetNextSceneContent(
  sceneId: string,
  scenePreviewId: string,
  options?: { limit?: number }
) {
  const { limit = 1 } = options ?? {};

  return useMutation(async ({ cursor }: { cursor: string }) => {
    return getSceneContents({
      sceneId,
      scenePreviewId,
      cursor,
      direction: 'after',
      limit
    });
  });
}

export function flattenSceneContents(
  data: ReturnType<typeof useGetSceneContents>['data']
): SceneContentsResponse {
  if (!data?.pages) return [];
  const flattened = data.pages.flatMap((page) => page.sceneContents ?? []);

  const seen = new Set<string>();
  const deduped: typeof flattened = [];
  for (const item of flattened) {
    const id =
      (item as { sceneChatId?: string }).sceneChatId ??
      (item as { sceneThreadId?: string }).sceneThreadId ??
      null;
    if (id && seen.has(id)) continue;
    if (id) seen.add(id);
    deduped.push(item);
  }

  // UI에서 최신이 아래로 오도록 sequence 오름차순으로 정렬
  deduped.sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
  return deduped;
}
